// Utils
import {
  calculateAverageExpectationValues,
  calculatePercentageAlignmentValue,
  getAvgGoalEvaluations,
  getClosestStep,
  getCultureScores,
  getSkillScores,
} from './evaluationHelpers'
import { notEmpty } from './typeGuards'
import { getAllowedContributors } from '../common/helpers/company.helper'
import { adaptBackendCompanyToFrontendCompany } from '../context/services/loadCompany.service'

// Types
import {
  ICompany,
  ICompanyMember,
  ICompanyPure,
} from '../common/models/Company'
import {
  IStatAssessor,
  IStatCardData,
} from '../components/evaluations/MemberStatBlocks'
import {
  EvaluationAssessmentType,
  IEvaluationAssessor,
  IEvaluationPure,
  IEvaluationState,
  ISingleEvaluationPure,
  SUBMITTED_KEY,
} from '../common/models/Evaluation'

// forms.goalsAchieved — mirrors the cohort definition in
// useEvaluationResultAverages so the per-member and aggregate goal counts agree.
const GOAL_ACHIEVED_STATUS = 3

// The pre-merged evaluation state the modal already holds: every assessment
// stamped with its sender's memberId, plus the merged assessor list.
export interface IAssessmentData {
  assessment: Array<ISingleEvaluationPure & { memberId: string }>
  assessors: IEvaluationAssessor[]
}

export interface IMemberStatData {
  skill: IStatCardData
  culture: IStatCardData
  goals: IStatCardData
  compareActive: boolean
  goalsAchieved: number
  goalsTotal: number
  assessors: {
    skill: IStatAssessor[]
    culture: IStatAssessor[]
    goals: IStatAssessor[]
  }
}

const averageOfMap = (map?: Map<string, number>) => {
  if (!map || map.size === 0) return undefined
  let total = 0
  map.forEach((v) => (total += v))
  return total / map.size
}

// How many leading segments are filled for a given average — the "fill whole
// blocks up to the closest step" model.
const segmentsFor = (
  values: Array<{ value: number; label: string }>,
  avg?: number
) =>
  typeof avg === 'number' && values.length
    ? {
        total: values.length,
        complete: values.filter(
          (v) => v.value <= getClosestStep(values, avg).value
        ).length,
      }
    : undefined

const stepLabel = (
  values: Array<{ value: number; label: string }>,
  avg?: number
) => (typeof avg === 'number' ? getClosestStep(values, avg).label : undefined)

/**
 * Pure score computation shared by the EvaluationModal member overview and the
 * collection member page. Takes every input as a parameter — no React, no
 * context, no side effects. `scoreCompany` is the company used both for the
 * allowed-contributor filter and the score scales (its settings)
 * The goals subtitle is intentionally left to the caller (it needs i18n) — the
 * raw `goalsAchieved` / `goalsTotal` are returned for it to format.
 */
export const getMemberStatData = (
  evaluation: IEvaluationPure | IEvaluationState | undefined,
  assessmentData: IAssessmentData,
  scoreCompany: ICompany | undefined,
  member: ICompanyMember,
  previousEvaluation?: IEvaluationState | IEvaluationPure,
  compareCompanyVersion?: ICompany | ICompanyPure
): IMemberStatData => {
  const skillValues = (scoreCompany && getSkillScores(scoreCompany)) || []
  const cultureValues = (scoreCompany && getCultureScores(scoreCompany)) || []

  const allowedAssessments =
    scoreCompany &&
    assessmentData.assessment.filter((a) =>
      getAllowedContributors(scoreCompany, member).some(
        (s) => s._id === a.senderId
      )
    )

  const skillScoreMap = allowedAssessments
    ? calculateAverageExpectationValues(
        allowedAssessments.filter((a) => a.skillsSubmitted)
      )
    : undefined
  const cultureScoreMap = allowedAssessments
    ? calculatePercentageAlignmentValue(
        allowedAssessments.filter((a) => a.cultureSubmitted)
      )
    : undefined

  // Previous-term maps for the "vs last" deltas.
  const prevCompany =
    compareCompanyVersion && !('allMembers' in compareCompanyVersion)
      ? adaptBackendCompanyToFrontendCompany(compareCompanyVersion)
      : compareCompanyVersion
  const prevMember =
    prevCompany?.members.find((m) => m._id === member._id) || member
  const prevAllowed =
    previousEvaluation && prevCompany
      ? previousEvaluation.assessment.filter((a) =>
          getAllowedContributors(prevCompany, prevMember).some(
            (s) => s._id === a.senderId
          )
        )
      : undefined
  const prevSkillsMap = prevAllowed
    ? calculateAverageExpectationValues(
        prevAllowed.filter((a) => a.skillsSubmitted)
      )
    : undefined
  const prevCultureMap = prevAllowed
    ? calculatePercentageAlignmentValue(
        prevAllowed.filter((a) => a.cultureSubmitted)
      )
    : undefined
  // Goals, like skills/culture, only count from allowed score contributors who
  // actually submitted — feed getAvgGoalEvaluations a shielded assessment list.
  const prevGoalEvals =
    previousEvaluation && prevAllowed
      ? getAvgGoalEvaluations({
          ...previousEvaluation,
          assessment: prevAllowed.filter((a) => a.goalsSubmitted),
        })
      : []

  const currentGoalEvals = evaluation
    ? getAvgGoalEvaluations({
        ...evaluation,
        assessment: (allowedAssessments || []).filter((a) => a.goalsSubmitted),
      })
    : []
  const goalsTotal = currentGoalEvals.length
  const goalsAchieved = currentGoalEvals.filter(
    (g) => g.goalStatus === GOAL_ACHIEVED_STATUS
  ).length

  const currentSkillAvg = averageOfMap(skillScoreMap)
  const currentCultureAvg = averageOfMap(cultureScoreMap)
  const currentGoalPct = goalsTotal
    ? (goalsAchieved / goalsTotal) * 100
    : undefined

  // When the previous term had zero submitted contributors for a section there
  // is no real previous average — leave it undefined so the delta pill and the
  // previous-term marker suppress (mirrors prevMap.size === 0 → undefined in
  // TermEvaluationsProgress). The prev maps already only include submitters.
  const prevSubmitted = (evalType: EvaluationAssessmentType) =>
    !!prevAllowed && prevAllowed.some((a) => a[SUBMITTED_KEY[evalType]])
  const prevSkillAvg =
    prevSubmitted('skills') && (prevSkillsMap?.size ?? 0) > 0
      ? averageOfMap(prevSkillsMap)
      : undefined
  const prevCultureAvg =
    prevSubmitted('culture') && (prevCultureMap?.size ?? 0) > 0
      ? averageOfMap(prevCultureMap)
      : undefined
  const prevGoalPct =
    prevSubmitted('goals') && prevGoalEvals.length
      ? (prevGoalEvals.filter((g) => g.goalStatus === GOAL_ACHIEVED_STATUS)
          .length /
          prevGoalEvals.length) *
        100
      : undefined

  const compareActive = !!previousEvaluation

  const membersMap = assessmentData.assessors.reduce((acc, assessor) => {
    const found = scoreCompany?.allMembers.find(
      (mem) => mem._id === assessor.memberId
    )
    if (found) acc.set(assessor.memberId, found)
    return acc
  }, new Map<string, ICompanyMember>())

  const assessorsFor = (evalType: EvaluationAssessmentType): IStatAssessor[] =>
    assessmentData.assessors
      .filter((a) => a[evalType])
      .map((a) => {
        const m = membersMap.get(a.memberId)
        if (!m) return undefined
        const assessment = assessmentData.assessment.find(
          (as) => as.senderId === a.memberId
        )
        return { member: m, submitted: !!assessment?.[SUBMITTED_KEY[evalType]] }
      })
      .filter(notEmpty)

  // A section only has a *score* when an allowed contributor submitted that type
  // — the same set the scores are computed from (allowedAssessments).
  const hasResultsFor = (evalType: EvaluationAssessmentType) =>
    !!allowedAssessments &&
    allowedAssessments.some((a) => a[SUBMITTED_KEY[evalType]])

  const assessors = {
    skill: assessorsFor('skills'),
    culture: assessorsFor('culture'),
    goals: assessorsFor('goals'),
  }

  return {
    skill: {
      value: currentSkillAvg,
      previous: prevSkillAvg,
      subtitle: stepLabel(skillValues, currentSkillAvg),
      segments: segmentsFor(skillValues, currentSkillAvg),
      assessors: assessors.skill,
      hasResults: hasResultsFor('skills'),
    },
    culture: {
      value: currentCultureAvg,
      previous: prevCultureAvg,
      subtitle: stepLabel(cultureValues, currentCultureAvg),
      segments: segmentsFor(cultureValues, currentCultureAvg),
      assessors: assessors.culture,
      hasResults: hasResultsFor('culture'),
    },
    goals: {
      value: currentGoalPct,
      previous: prevGoalPct,
      assessors: assessors.goals,
      hasResults: hasResultsFor('goals'),
    },
    compareActive,
    goalsAchieved,
    goalsTotal,
    assessors,
  }
}
